import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import type { ReactNode } from "react";
import { Spinner } from "@/components/ui/Spinner";
import { useFloatingPanel, estimateAutocompletePanelHeight, floatingPanelStyle } from "@/lib/useFloatingPanel";

export interface AutocompleteOption<T> {
  /** Eindeutiger Schlüssel pro Vorschlag. */
  key: string;
  /** Freitext, der im Eingabefeld erscheint. */
  label: string;
  /** Optionaler Hinweis (zweite Zeile, kleiner gesetzt). */
  description?: string;
  /** Original-Datensatz, wird beim Auswählen zurückgegeben. */
  value: T;
}

export interface AutocompleteProps<T> {
  label?: ReactNode;
  hint?: ReactNode;
  placeholder?: string;
  /** Anfangswert des Eingabefelds (Label). */
  initialQuery?: string;
  /** Liefert Vorschläge zur aktuellen Eingabe. Wird debounced aufgerufen. */
  loadOptions: (query: string) => Promise<AutocompleteOption<T>[]>;
  /** Wird aufgerufen, wenn der User einen Vorschlag wählt. */
  onSelect: (option: AutocompleteOption<T> | null) => void;
  /** Optional: minimale Eingabelänge, ab der gesucht wird (Default 0). */
  minChars?: number;
  /** Optional: Debounce in ms (Default 200). */
  debounceMs?: number;
  /** Optional: Anzeigentext, wenn nichts gefunden wird. */
  emptyText?: string;
  className?: string;
  required?: boolean;
  /** Pflichtfehler nach unten reichen. */
  error?: ReactNode;
  /** Nach Auswahl Eingabefeld leeren statt Label zu übernehmen (z. B. wiederholte Suche). */
  clearOnSelect?: boolean;
}

/**
 * Schlanke Autocomplete-Komponente ohne Headless-UI-Combobox.
 *
 * - Tippt der User, wird `loadOptions(q)` debounced aufgerufen.
 * - Vorschläge erscheinen unterhalb des Felds; Pfeil-Tasten + Enter wählen aus.
 * - Wenn der User das Feld verändert, ohne einen Vorschlag zu wählen, wird die
 *   bestehende Auswahl verworfen (`onSelect(null)`), bis er erneut auswählt.
 */
export function Autocomplete<T>({
  label,
  hint,
  placeholder,
  initialQuery = "",
  loadOptions,
  onSelect,
  minChars = 0,
  debounceMs = 200,
  emptyText = "Keine Treffer",
  className,
  required,
  error,
  clearOnSelect = false,
}: AutocompleteProps<T>) {
  const id = useId();
  const [query, setQuery] = useState(initialQuery);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AutocompleteOption<T>[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const closeTimer = useRef<number | null>(null);
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;

  const trimmed = query.trim();
  const canSearch = trimmed.length >= minChars;

  // Debounce-Loading. loadOptions bewusst per Ref, damit ein neues
  // Callback-Objekt des Parents eine laufende Suche nicht abbricht.
  useEffect(() => {
    if (!open) return;
    if (!canSearch) {
      setOptions([]);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const handle = window.setTimeout(async () => {
      try {
        const list = await loadOptionsRef.current(trimmed);
        if (!cancelled) {
          setOptions(list || []);
          setActiveIndex(list && list.length ? 0 : -1);
          setLoadError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setOptions([]);
          const message = err instanceof Error ? err.message.trim() : "";
          setLoadError(message || "Suche fehlgeschlagen");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, debounceMs);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [trimmed, open, canSearch, debounceMs]);

  function handleSelect(option: AutocompleteOption<T>) {
    setQuery(clearOnSelect ? "" : option.label);
    setOpen(false);
    setOptions([]);
    onSelect(option);
    if (clearOnSelect) {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  const noOptions = useMemo(
    () => !loading && !loadError && options.length === 0 && canSearch && open,
    [loading, loadError, options.length, canSearch, open],
  );

  const showPanel = open && (loading || options.length > 0 || noOptions || Boolean(loadError));
  const panelHeightEstimate = useMemo(
    () =>
      estimateAutocompletePanelHeight({
        optionCount: options.length,
        loading,
        empty: noOptions || Boolean(loadError),
        hasDescription: options.some((opt) => Boolean(opt.description)),
        maxHeightCap: 320,
      }),
    [loading, loadError, noOptions, options],
  );
  const floating = useFloatingPanel(inputRef, showPanel, {
    gap: 4,
    maxHeightCap: 320,
    minBelow: 180,
    contentHeightEstimate: panelHeightEstimate,
  });

  // Bei Flip (Liste oberhalb): bester Treffer nah an der Eingabe halten.
  const optionEntries = useMemo(() => {
    const entries = options.map((opt, idx) => ({ opt, idx }));
    return floating?.flipped ? [...entries].reverse() : entries;
  }, [options, floating?.flipped]);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || !floating?.flipped) return;
    el.scrollTop = el.scrollHeight;
  }, [floating?.flipped, optionEntries, loading]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (activeIndex < 0 || !list) return;
    const activeEl = list.querySelector<HTMLElement>(`[data-ac-idx="${activeIndex}"]`);
    if (!activeEl) return;
    // Nur die Liste scrollen – nicht die Seite (wichtig auf Mobil).
    const top = activeEl.offsetTop;
    const bottom = top + activeEl.offsetHeight;
    if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }, [activeIndex]);

  const statusRow = loading ? (
    <li key="status" className="flex items-center gap-2 px-3 py-2 text-label-tertiary">
      <Spinner className="h-3.5 w-3.5" />
      Suche …
    </li>
  ) : loadError ? (
    <li key="status" className="whitespace-normal break-words px-3 py-2 text-status-red">
      {loadError}
    </li>
  ) : noOptions ? (
    <li key="status" className="px-3 py-2 text-label-tertiary">
      {emptyText}
    </li>
  ) : null;
  const optionRows = optionEntries.map(({ opt, idx }) => (
    <li
      key={opt.key}
      data-ac-idx={idx}
      role="option"
      aria-selected={idx === activeIndex}
      onMouseEnter={() => setActiveIndex(idx)}
      onMouseDown={(e) => {
        e.preventDefault();
        handleSelect(opt);
      }}
      className={clsx(
        "cursor-pointer px-3 py-1.5",
        idx === activeIndex ? "bg-accent text-label-on-accent" : "text-label",
      )}
    >
      <div className="font-medium">{opt.label}</div>
      {opt.description && (
        <div
          className={clsx(
            "text-xs",
            idx === activeIndex ? "text-label-on-accent/80" : "text-label-secondary",
          )}
        >
          {opt.description}
        </div>
      )}
    </li>
  ));
  // Flip: bester Treffer / Status nah an der Eingabe (unten im Panel).
  const listContent = floating?.flipped ? (
    <>
      {optionRows}
      {statusRow}
    </>
  ) : (
    <>
      {statusRow}
      {optionRows}
    </>
  );

  return (
    <div className={clsx("space-y-1", className)}>
      {label && (
        <label className="eh-label" htmlFor={id}>
          {label}
          {required ? <span className="ml-1 text-status-red">*</span> : null}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          autoComplete="off"
          className={clsx(
            "eh-input",
            loading && "pr-9",
            error && "border-status-red focus:border-status-red",
          )}
          placeholder={placeholder}
          value={query}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Erst nach kurzem Delay schließen, damit Mausklicks auf Optionen feuern können.
            if (closeTimer.current) window.clearTimeout(closeTimer.current);
            closeTimer.current = window.setTimeout(() => setOpen(false), 120);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            onSelect(null);
          }}
          onKeyDown={(e) => {
            if (!open) {
              if (e.key === "ArrowDown") {
                setOpen(true);
                e.preventDefault();
              }
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIndex((idx) => (idx + 1) % Math.max(options.length, 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIndex((idx) => (idx <= 0 ? options.length - 1 : idx - 1));
            } else if (e.key === "Enter") {
              if (options[activeIndex]) {
                e.preventDefault();
                handleSelect(options[activeIndex]);
              }
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={`${id}-listbox`}
          aria-required={required || undefined}
          aria-busy={loading || undefined}
        />
        {loading ? (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-label-tertiary"
          >
            <Spinner className="h-4 w-4" />
          </span>
        ) : null}
        {showPanel && floating && typeof document !== "undefined"
          ? createPortal(
              <ul
                ref={listRef}
                id={`${id}-listbox`}
                role="listbox"
                className="z-[1000] overflow-auto rounded-lg border border-separator bg-card py-1 text-sm shadow-ios-md"
                style={floatingPanelStyle(floating)}
                onMouseDown={(e) => e.preventDefault()}
              >
                {listContent}
              </ul>,
              document.body,
            )
          : null}
      </div>
      {hint && !error && <p className="text-xs text-label-secondary">{hint}</p>}
      {error && <p className="text-xs text-status-red">{error}</p>}
    </div>
  );
}
